import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';

const FilterBarLayout = {
  menuShadow: '0px 6px 16px rgba(0, 0, 0, 0.12)',
  optionHeight: 39,
  optionGap: 2,
  maxVisibleOptions: 5,
  border: '#CFCFD0',
  selectedOptionBg: '#E9F1EF',
  optionText: '#3F4040',
};

/** Max height for ~5 option rows + gaps between them */
const OPTIONS_SCROLL_MAX_HEIGHT = `calc(${FilterBarLayout.maxVisibleOptions} * ${
  FilterBarLayout.optionHeight
}px + ${FilterBarLayout.maxVisibleOptions - 1} * ${FilterBarLayout.optionGap}px)`;

type FilterPillState = {
  $active?: boolean;
  $open?: boolean;
  $disabled?: boolean;
};

export const StFilterBar = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  backgroundColor: theme.palette.white?.main || theme.palette.neutral0?.main,
}));

export const StFilterBarInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 48px',
  gap: '24px',
  width: '100%',
  maxWidth: '1366px',
  margin: '0 auto',
  boxSizing: 'border-box',
  [theme.breakpoints.down('lg')]: {
    padding: '16px 24px',
    flexWrap: 'wrap',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
}));

export const StFilterPills = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  padding: 0,
  gap: '12px',
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
  },
}));

export const StFilterPill = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$open' && prop !== '$disabled',
})<FilterPillState>(({ theme, $active, $open, $disabled }) => {
  const isHighlighted = Boolean(($active || $open) && !$disabled);
  return {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8px 16px',
    height: '40px',
    background: isHighlighted ? theme.palette.themeColor?.main : theme.palette.neutral2?.main,
    border: `1px solid ${
      isHighlighted ? theme.palette.themeColor?.main : theme.palette.neutral4?.main
    }`,
    borderRadius: '320px',
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.5 : 1,
    fontFamily: theme.typography.fontFamily,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    color: isHighlighted ? theme.palette.white?.main : theme.palette.hintText?.main,
    '& .MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
      marginLeft: '8px',
      color: isHighlighted ? theme.palette.white?.main : theme.palette.neutral12?.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.themeColor?.main}`,
      outlineOffset: '2px',
    },
  };
});

export const StFilterCountBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '20px',
  height: '20px',
  marginLeft: '8px',
  padding: '0 6px',
  borderRadius: '320px',
  backgroundColor: theme.palette.white?.main,
  color: theme.palette.themeColor?.main,
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
}));

export const StFilterMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: 0,
  gap: '12px',
  flexGrow: 1,
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    justifyContent: 'space-between',
    flexGrow: 0,
  },
}));

export const StClearFiltersButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  textDecoration: 'underline',
  color: theme.palette.themeColor?.main,
  '&:hover': {
    opacity: 0.85,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
    color: theme.palette.neutral11?.main,
    '&:hover': {
      opacity: 0.5,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.themeColor?.main}`,
    outlineOffset: '2px',
  },
}));

export const StFilterMenuPaper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$withFooter' && prop !== '$scrollable',
})<{ $withFooter?: boolean; $scrollable?: boolean }>(({ theme, $withFooter, $scrollable }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '8px',
  gap: `${FilterBarLayout.optionGap}px`,
  width: '290px',
  height: $withFooter && $scrollable ? '266px' : 'auto',
  maxHeight: $withFooter && $scrollable ? '266px' : undefined,
  background: theme.palette.white?.main,
  border: `1px solid ${FilterBarLayout.border}`,
  boxShadow: FilterBarLayout.menuShadow,
  borderRadius: '12px',
  overflow: 'hidden',
  zIndex: theme.zIndex.modal,
  transformOrigin: 'top left',
}));

export const StFilterMenuEmpty = styled(Box)(() => ({
  boxSizing: 'border-box',
  padding: '8px 16px',
  width: '100%',
}));

export const StFilterMenuOptions = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$scrollable',
})<{ $scrollable?: boolean }>(({ $scrollable }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: `${FilterBarLayout.optionGap}px`,
  width: '100%',
  flex: $scrollable ? '1 1 auto' : '0 0 auto',
  minHeight: 0,
  maxHeight: $scrollable ? OPTIONS_SCROLL_MAX_HEIGHT : undefined,
  overflowY: $scrollable ? 'auto' : 'visible',
  overflowX: 'hidden',
}));

export const StFilterMenuOption = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>(({ theme, $selected }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: 0,
  padding: '10px 16px',
  gap: '12px',
  width: '100%',
  height: `${FilterBarLayout.optionHeight}px`,
  alignSelf: 'stretch',
  flex: 'none',
  overflow: 'visible',
  backgroundColor: $selected ? FilterBarLayout.selectedOptionBg : theme.palette.white?.main,
  borderRadius: '6px',
  color: FilterBarLayout.optionText,
  '&:hover': {
    backgroundColor: FilterBarLayout.selectedOptionBg,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.themeColor?.main}`,
    outlineOffset: '-2px',
  },

  '& .MuiFormControlLabel-label': {
    fontFamily: theme.typography.fontFamily,
    fontStyle: 'normal',
    fontWeight: $selected ? 500 : 400,
    fontSize: '14px',
    lineHeight: '19px',
    color: FilterBarLayout.optionText,
  },
  '& .MuiButtonBase-root': {
    padding: 0,
    marginRight: 0,
  },
  '& .MuiCheckbox-root': {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    width: '18px',
    height: '18px',
    overflow: 'visible',
    color: theme.palette.themeColor?.main,
  },
  '& .MuiRadio-root': {
    padding: 0,
    width: '18px',
    height: '18px',
    color: FilterBarLayout.border,
    '& .MuiSvgIcon-root': {
      width: '18px',
      height: '18px',
    },
    '&.Mui-checked': {
      color: theme.palette.themeColor?.main,
    },
  },
}));

export const StFilterCheckboxUnchecked = styled('span')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  width: '18px',
  height: '18px',
  background: theme.palette.white?.main,
  border: `1.5px solid ${FilterBarLayout.border}`,
  borderRadius: '4px',
}));

export const StFilterCheckboxChecked = styled('span')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 'none',
  padding: 0,
  width: '18px',
  height: '18px',
  background: theme.palette.themeColor?.main,
  borderRadius: '4px',
  '&::after': {
    content: '""',
    width: '4px',
    height: '8px',
    border: '2px solid #FFFFFF',
    borderTop: 0,
    borderLeft: 0,
    transform: 'rotate(45deg)',
    marginTop: '-1px',
  },
}));

export const StFilterMenuDivider = styled(Box)(() => ({
  width: '100%',
  height: '1px',
  background: FilterBarLayout.border,
  flex: 'none',
  alignSelf: 'stretch',
}));

export const StFilterMenuActions = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 8px 4px',
  width: '100%',
  height: '42px',
  boxSizing: 'border-box',
  flex: 'none',
  alignSelf: 'stretch',
}));

export const StFilterMenuActionButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$primary',
})<{ $primary?: boolean }>(({ theme, $primary }) => ({
  border: 'none',
  cursor: 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  ...($primary
    ? {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 16px',
        height: '32px',
        background: theme.palette.themeColor?.main,
        borderRadius: '320px',
        fontWeight: 600,
        fontSize: '13px',
        lineHeight: '18px',
        color: theme.palette.white?.main,
        textDecoration: 'none',
      }
    : {
        padding: 0,
        background: 'transparent',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '19px',
        color: theme.palette.themeColor?.main,
        textDecoration: 'none',
      }),
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.themeColor?.main}`,
    outlineOffset: '2px',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
    ...($primary
      ? { background: theme.palette.neutral11?.main }
      : { color: theme.palette.neutral11?.main }),
  },
}));
